import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { X, Loader2, Check, Ban, ShieldAlert, HandCoins } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin-side bids panel. Renders over the Shifts page as a modal.
 * Shows every bid on a given shift, flags cert issues, and lets the admin
 * approve one (auto-rejects the rest) or reject individuals.
 */
export default function BidsPanel({ shift, onClose, onChange }) {
  const [data, setData] = useState(null);
  const [busyUserId, setBusyUserId] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/shifts/${shift.id}/bids`);
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [shift.id]);

  const approve = async (uid, name) => {
    if (!window.confirm(`Approve ${name}? All other pending bids on this shift will be auto-rejected.`)) return;
    setBusyUserId(uid);
    try {
      await api.post(`/shifts/${shift.id}/bids/${uid}/approve`);
      toast.success(`${name} assigned to this shift`);
      onChange && onChange();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyUserId(null); }
  };

  const reject = async (uid, name) => {
    setBusyUserId(uid);
    try {
      await api.post(`/shifts/${shift.id}/bids/${uid}/reject`);
      toast.success(`${name}'s bid rejected`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyUserId(null); }
  };

  if (!data) {
    return (
      <div className="accp-modal-shell" onClick={onClose}>
        <div className="accp-modal-card text-center text-muted-foreground">
          <Loader2 size={16} className="animate-spin inline mr-2" /> Loading…
        </div>
      </div>
    );
  }

  return (
    <div className="accp-modal-shell" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="accp-modal-card accp-modal-card-lg space-y-4"
           data-testid="bids-panel">
        <div className="flex items-center justify-between">
          <div>
            <div className="accp-label flex items-center gap-2"><HandCoins size={12}/> Bids panel</div>
            <h3 className="text-xl font-semibold text-primary" style={{ fontFamily: "Outfit" }}>
              {shift.code} · {formatDate(shift.start_time)}
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Approving one bid auto-rejects the rest and assigns the shift.
            </p>
          </div>
          <button onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>

        {data.shift?.auto_filled ? (
          <div className="rounded-xl compliance-valid p-4 text-sm" data-testid="bids-auto-filled-banner">
            <div className="font-semibold text-[#4A7C1E]">
              <Check size={14} className="inline -mt-0.5 mr-1" />
              Auto-assigned to {data.bids.find((b) => b.status === "approved")?.user_name || "a qualified bidder"}
            </div>
            {data.shift.auto_filled_at && (
              <div className="text-xs mt-1 text-[#4A7C1E]/80">
                on {new Date(data.shift.auto_filled_at).toLocaleString()} · this shift is no longer open for review
              </div>
            )}
          </div>
        ) : data.bids.length === 0 ? (
          <div className="rounded-xl bg-accent/40 p-6 text-center text-sm text-muted-foreground" data-testid="bids-empty">
            No bids yet. DSPs will see this shift on their Open Shifts page.
          </div>
        ) : (
          <ul className="space-y-2">
            {data.bids.map((b) => (
              <li key={b.user_id}
                  className="rounded-xl border border-border p-3 flex items-center justify-between gap-3"
                  data-testid={`bid-row-${b.user_code}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-primary">{b.user_name}</span>
                    <span className="text-xs text-muted-foreground">{b.user_code}</span>
                    <StatusPill status={b.status} />
                    {b.has_expired_cert && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full compliance-expired"
                            data-testid={`bid-warn-${b.user_code}`}>
                        <ShieldAlert size={10}/> Expired cert
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    Bid at {new Date(b.bid_at).toLocaleString()} · rate ${(b.hourly_rate || 0).toFixed(2)}/h
                  </div>
                </div>
                {b.status === "pending" && data.shift?.status === "unassigned" && (
                  <div className="flex gap-1">
                    <button
                      className="accp-btn-ghost text-xs"
                      disabled={busyUserId === b.user_id}
                      onClick={() => reject(b.user_id, b.user_name)}
                      data-testid={`reject-${b.user_code}`}
                    >
                      {busyUserId === b.user_id ? <Loader2 size={12} className="animate-spin"/> : <Ban size={12} />}
                      Reject
                    </button>
                    <button
                      className="accp-btn-primary text-xs !py-1.5 !px-3"
                      disabled={busyUserId === b.user_id}
                      onClick={() => approve(b.user_id, b.user_name)}
                      data-testid={`approve-${b.user_code}`}
                    >
                      {busyUserId === b.user_id ? <Loader2 size={12} className="animate-spin"/> : <Check size={12} />}
                      Approve
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const styles = {
    pending: "bg-[#EEEBF5] text-[#5B3AA4]",
    approved: "compliance-valid",
    rejected: "bg-[#FAD7D7] text-[#B71C1C]",
  };
  return (
    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${styles[status] || ""}`}>
      {status}
    </span>
  );
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}
